import styled from 'styled-components'

import { Card, Pill } from '@baseV2'
import { Devices, Spacings, Text } from '@styles'

/* One-off styles for the Tenderbook user detail tabs. Anything reusable beyond this
   feature lives in @baseV2's styled folder instead. */

// Centres the loader / "user not found" message inside the content area.
export const CenteredState = styled.div`
	display: flex;
	flex: 1;
	align-items: center;
	justify-content: center;
	padding: ${Spacings.SPACING_8B} 0;
`

/* ─── Card shells (matching Manage Enquiry's view cards) ───────────────── */

export const FullHeightCard = styled(Card)`
	flex: 1;
`

export const AlignedCardHeader = styled(Card.Header)`
	align-items: center;
	min-height: ${Spacings.SPACING_7B};
`

// Card title that can carry a leading icon, tinted to the brand colour.
export const CardTitleWithIcon = styled(Card.Title)`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};

	svg {
		font-size: ${Text.X_LARGE};
		color: ${({ theme }) => theme.colors.brand.primary};
	}
`

export const HeaderPills = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_2B};
`

// Vertical stack of cards down the tab.
export const CardColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
`

// Cards side by side at laptop+, stacked below it. `align-items: stretch` keeps a short
// card the same height as a tall one on the same row.
export const CardRow = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: ${Spacings.SPACING_4B};

	@media ${Devices.laptop} {
		flex-direction: row;

		& > * {
			min-width: 0;
		}
	}
`

// Stacks a full-width card list (subscriptions, saved filters) inside a section.
export const StackList = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
`

// Wraps a free-flowing row of pills (plans, features, keywords).
export const TagRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
`

/* ─── Overview: metric tiles + chart frames ────────────────────────────── */

export const MetricRow = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: ${Spacings.SPACING_3B};

	@media ${Devices.tablet} {
		grid-template-columns: repeat(${({ $columns }) => $columns || 4}, 1fr);
	}
`

// Label / value on the left, a tinted leaf carrying the icon bleeding off the right edge —
// the same stat card the Organisation Mbook dashboard uses.
export const MetricTile = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.lg};
	background: ${({ theme }) => theme.colors.surface.card};
	box-shadow: ${({ theme }) => theme.shadows.sm};
	overflow: hidden;
`

export const MetricText = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1B};
	min-width: 0;
`

export const MetricLabel = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.REGULAR};
	color: ${({ theme }) => theme.colors.text.secondary};
`

export const MetricValue = styled.span`
	font-size: ${Text.X_LARGE};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};
`

// The leaf is a wide ellipse anchored to the tile's left edge and clipped by it, so only its
// rounded left side shows. `$tone` is a theme.colors.status key.
export const MetricIcon = styled.div`
	position: relative;
	flex: 0 0 auto;
	align-self: stretch;
	width: ${Spacings.SPACING_14B};
	margin: -${Spacings.SPACING_3B} -${Spacings.SPACING_4B} -${Spacings.SPACING_3B} 0;
	display: flex;
	align-items: center;
	justify-content: center;

	&:before {
		content: '';
		position: absolute;
		left: 0;
		top: 50%;
		transform: translateY(-50%);
		width: 16rem;
		height: 12rem;
		border-radius: 60%;
		background: ${({ theme, $tone }) => (theme.colors.status[$tone] || theme.colors.status.success).bg};
	}

	svg {
		position: relative;
		font-size: ${Text.X_LARGE};
		color: ${({ theme, $tone }) => (theme.colors.status[$tone] || theme.colors.status.success).icon};
	}
`

// Fixed-height frame so ResponsiveContainer has something to measure against.
export const ChartFrame = styled.div`
	width: 100%;
	height: ${({ $height }) => $height || '260px'};
`

/* ─── Subscription cards ───────────────────────────────────────────────── */

// Nested card for one subscription / saved filter. Sits on the sunken surface so it reads
// as a row inside its parent card rather than a second card floating on the same plane.
export const SubscriptionCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
	background: ${({ theme }) => theme.colors.surface.sunken};
`

export const SubscriptionHead = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_2B};
	padding-bottom: ${Spacings.SPACING_2B};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`

export const PlanName = styled.span`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};
	text-transform: capitalize;
`

/* ─── Feature / toggle rows ────────────────────────────────────────────── */

// A feature line: label on the left, "on/off" pill plus its date on the right.
export const FeatureRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_2B} 0;
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

	&:last-child {
		border-bottom: none;
	}
`

export const FeatureLabel = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.primary};
`

export const FeatureValue = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

/* ─── Tender activity table cells ──────────────────────────────────────── */

export const StackCell = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1};
	min-width: 0;
`

export const PrimaryText = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.text.primary};

	/* Tender summaries run long — clamp so rows stay a readable height. */
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
`

export const SecondaryText = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

/* ─── Entity cards (tenders, companies) ────────────────────────────────── */

// The shared card shell. Opening the record is an explicit button, not a click on the card.
export const EntityCardWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.lg};
	background: ${({ theme }) => theme.colors.surface.card};
`

// Content column on the left, the meta boxes / actions on the right.
export const EntityCardBody = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: ${Spacings.SPACING_3B};

	@media ${Devices.tablet} {
		flex-direction: row;
		align-items: flex-start;
		justify-content: space-between;
	}
`

export const EntityCardMain = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
	flex: 1;
	min-width: 0;
`

export const TenderCardHead = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_2B};
`

export const TenderAuthority = styled.span`
	font-size: ${Text.LARGE};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};
`

// An id (tender id, GSTIN) copied to the clipboard on click.
export const CopyableId = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
	font-size: ${Text.EXTRA_SMALL};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.secondary};
	letter-spacing: 0.02em;
	cursor: copy;
	text-decoration: underline dotted;
	text-underline-offset: 2px;

	&:hover {
		color: ${({ theme }) => theme.colors.brand.primary};
	}

	svg {
		font-size: ${Text.SMALL};
	}
`

// Summaries run long — clamp so a card stays a predictable height.
export const TenderSummary = styled.p`
	margin: 0;
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};

	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
`

// Square-ish tag, unlike the fully rounded default pill used elsewhere on these tabs.
export const TenderTag = styled(Pill)`
	border-radius: ${({ theme }) => theme.radius.md};
	white-space: normal;
	text-align: left;
`

// Amount / closing-date boxes with the open-on-Tenderbook action beneath them.
export const TenderSideColumn = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: ${Spacings.SPACING_2B};
	flex: 0 0 auto;

	@media ${Devices.tablet} {
		align-items: flex-end;
	}
`

/* ─── Company cards (companies viewed) ─────────────────────────────────── */

// Icon tile + name / GSTIN, mirroring the Tenderbook company row.
export const CompanyIdentity = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_3B};
	min-width: 0;
`

export const CompanyIconBox = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
	width: ${Spacings.SPACING_10B};
	height: ${Spacings.SPACING_10B};
	border-radius: ${({ theme }) => theme.radius.md};
	background: ${({ theme }) => theme.colors.status.info.bg};

	svg {
		font-size: ${Text.X_LARGE};
		color: ${({ theme }) => theme.colors.status.info.icon};
	}
`

export const CompanyNameColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1};
	min-width: 0;
`

export const CompanyName = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};
	text-transform: capitalize;
`

export const TenderMetaBoxes = styled.div`
	display: flex;
	align-items: stretch;
	gap: ${Spacings.SPACING_2B};
	flex: 0 0 auto;
`

// Amount / closing-date callout, tinted by `$tone` (a theme.colors.status key).
export const TenderMetaBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1};
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
	min-width: ${Spacings.SPACING_24B};
	border: 1px solid ${({ theme, $tone }) => (theme.colors.status[$tone] || theme.colors.status.neutral).border};
	border-radius: ${({ theme }) => theme.radius.md};
	background: ${({ theme, $tone }) => (theme.colors.status[$tone] || theme.colors.status.neutral).bg};
`

export const TenderMetaLabel = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme, $tone }) => (theme.colors.status[$tone] || theme.colors.status.neutral).text};
`

export const TenderMetaValue = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};
`
